import { gamificationStore } from '../../../utils/gamificationStore';

export class LessonGamificationService {
  private fireAward(
    actionKey: string,
    payload: any,
    optimisticPoints: number,
    onCorrection?: (correction: number) => void,
    onResult?: (actualPoints: number) => void
  ) {
    gamificationStore.getState().setAward(actionKey, payload).then(deltaResult => {
      const actualDelta = deltaResult?.delta ?? optimisticPoints;
      if (onResult) {
        onResult(actualDelta);
      }
      if (actualDelta !== optimisticPoints && onCorrection) {
        onCorrection(actualDelta - optimisticPoints);
      }
    }).catch(error => {
      console.error(`LessonGamificationService: Failed to award ${actionKey}`, error);
      if (onResult) {
        onResult(0);
      }
      if (onCorrection) {
        onCorrection(-optimisticPoints);
      }
    });
  }

  public awardVideoWatched(
    lessonId: number | undefined,
    phaseIndex: number,
    onResult?: (actualPoints: number) => void,
    onCorrection?: (correction: number) => void
  ): number {
    const optimisticPoints = gamificationStore.getState().pointsConfig['video_watched'] || 20;
    this.fireAward('video_watched', { lesson_id: lessonId, phase_index: phaseIndex }, optimisticPoints, onCorrection, onResult);
    return optimisticPoints;
  }

  public awardStepComplete(
    lessonId: number | undefined,
    stepIndex: number,
    phaseIndex: number,
    onCorrection?: (correction: number) => void
  ): number {
    const optimisticPoints = gamificationStore.getState().pointsConfig['step_complete'] || 10;
    this.fireAward('step_complete', { lesson_id: lessonId, step_index: stepIndex, phase_index: phaseIndex }, optimisticPoints, onCorrection);
    return optimisticPoints;
  }

  public penalizeHintUsed(
    lessonId: number | undefined,
    stepIndex: number,
    phaseIndex: number,
    onCorrection?: (correction: number) => void
  ): number {
    const optimisticPoints = gamificationStore.getState().pointsConfig['hint_used'] || -5;
    this.fireAward('hint_used', { lesson_id: lessonId, step_index: stepIndex, phase_index: phaseIndex }, optimisticPoints, onCorrection);
    return optimisticPoints;
  }

  public awardQuizCorrect(
    lessonId: number | undefined,
    stepIndex: number,
    phaseIndex: number,
    isHintUsed: boolean,
    streakCount: number,
    onResult?: (actualPoints: number) => void,
    onCorrection?: (correction: number) => void
  ): { optimisticPoints: number; achievedStreak: number } {
    const pointsConfig = gamificationStore.getState().pointsConfig;
    const actionKey = isHintUsed ? 'quiz_correct_with_hint' : 'quiz_correct';
    const optimisticBase = pointsConfig[actionKey] || (isHintUsed ? 10 : 20);

    let streakBonusKey: string | null = null;
    let achievedStreak = 0;
    if (streakCount === 3) { streakBonusKey = 'quiz_correct_streak_3'; achievedStreak = 3; }
    else if (streakCount === 5) { streakBonusKey = 'quiz_correct_streak_5'; achievedStreak = 5; }
    else if (streakCount === 10) { streakBonusKey = 'quiz_correct_streak_10'; achievedStreak = 10; }

    const optimisticBonus = streakBonusKey 
      ? (pointsConfig[streakBonusKey] || (streakCount === 3 ? 10 : streakCount === 5 ? 20 : 50)) 
      : 0;

    // Fire base award
    this.fireAward(actionKey, { lesson_id: lessonId, step_index: stepIndex, phase_index: phaseIndex }, optimisticBase, onCorrection, onResult);

    // Fire streak bonus award if applicable
    if (streakBonusKey) {
      this.fireAward(streakBonusKey, { lesson_id: lessonId, step_index: stepIndex, phase_index: phaseIndex }, optimisticBonus, onCorrection, onResult);
    }

    return {
      optimisticPoints: optimisticBase + optimisticBonus,
      achievedStreak
    };
  }
}

export const lessonGamificationService = new LessonGamificationService();
// Agent synced
